import { Box, makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { useEnabledWorkingDaysLength } from "Components/CompanySetting/CompanySettingHelper";
import { color, px } from "Configs/theme";
import { getColorRgbaFromHex } from "Helpers/ColorHelper";
import { useTranslation } from "Providers/TranslationProvider";
import moment from "moment";
import React, { FC } from "react";
import { CalendarType } from "./NavigationHelper";

interface Props {
	cellSize: CellSize;
}

const NavigationCalendarMonthView: FC<Props> = (props) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const daysLength = useEnabledWorkingDaysLength();
	const startOfWeek = useCalendar((state) => state.startOfWeek);

	const { t } = useTranslation();
	const classes = useStyles({
		nameWidth: props.cellSize.month.nameWidth,
		dayWidth: props.cellSize.month.dayWidth,
		navigationCalendarHeight: props.cellSize.navigation.calendarHeight,
		daysLength,
	});

	function renderDays(week: moment.Moment) {
		const days = Array(daysLength).fill(null);

		return days.map((_, index) => {
			const day = week.clone().add(index, "days");

			return (
				<Box key={index} className={classes.dayColumn}>
					{day.format("DD")}
				</Box>
			);
		});
	}

	function renderDayBorders() {
		const days = Array(daysLength).fill(null);

		return days.map((_, index) => (
			<Box key={index} className={classes.dayBorderColumn} />
		));
	}

	function renderWeeks() {
		const weeks = Array(props.cellSize.month.weeksLength).fill(null);
		const currentWeek = moment().startOf("week");

		return weeks.map((_, index) => {
			const week = startOfWeek.clone().add(index, "weeks");

			return (
				<Box key={index} className={classes.weekColumn}>
					<Box
						className={classes.weekBox}
						onClick={() => {
							useCalendar
								.getState()
								.setWeekPlus(week.diff(currentWeek, "weeks"));
							useCalendar.getState().setCalendarType(CalendarType.WEEK);
						}}
						color={
							currentWeek.isSame(week, "week") ? color.primary : color.lightText
						}
					>
						<Box className={classes.weekCw}>
							{t("Common_Week", { week: week.week() })}
						</Box>
						{!isSmallScreen && (
							<Box className={classes.weekFromTo}>
								{week.format("MMM DD")}
								{" - "}
								{week
									.clone()
									.add(daysLength - 1, "days")
									.format("MMM DD")}
							</Box>
						)}
						<Box className={classes.weekZoom}>
							<ZoomInIcon fontSize={isSmallScreen ? "default" : "large"} />
						</Box>
					</Box>
					<Box className={classes.daysRow}>{renderDays(week)}</Box>
					<Box className={classes.dayBordersRow}>{renderDayBorders()}</Box>
				</Box>
			);
		});
	}

	return (
		<Box className={classes.root}>
			<Box className={classes.mainColumn} />
			{renderWeeks()}
		</Box>
	);
};

export default NavigationCalendarMonthView;

interface StyleProps {
	nameWidth: number;
	dayWidth: number;
	daysLength: number;
	navigationCalendarHeight: number;
}

const useStyles = makeStyles({
	root: {
		display: "flex",
		borderBottom: `1px solid ${color.tableCellBorder}`,
	},
	mainColumn: (props: StyleProps) => ({
		display: "flex",
		flexDirection: "column",
		height: props.navigationCalendarHeight,
		width: props.nameWidth - 1,
	}),
	weekColumn: (props: StyleProps) => ({
		display: "flex",
		flexDirection: "column",
		borderLeft: `2px solid ${color.tableCellBorder}`,
		borderRight: `1px solid ${color.tableCellBorder}`,
		letterSpacing: "1px",
		textTransform: "uppercase",
		width: props.dayWidth * props.daysLength - 3,
		"&:nth-child(2)": {
			borderLeftWidth: 3,
		},
	}),
	weekBox: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		textAlign: "center",
		cursor: "pointer",
		position: "relative",
		flex: 1,
		"&:hover": {
			"& > $weekCw, & > $weekFromTo": {
				opacity: 0.2,
				transform: "scale(0.9)",
			},
			"& > $weekZoom > svg": {
				opacity: 1,
				transform: "scale(1)",
			},
		},
	},
	weekCw: {
		fontSize: "20px",
		fontWeight: 500,
		transition: "all 250ms ease",
	},
	weekFromTo: {
		fontSize: "10px",
		transition: "all 250ms ease",
	},
	weekZoom: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		"& > svg": {
			transition: "all 250ms ease",
			transform: "scale(0.5)",
			opacity: 0,
		},
	},
	daysRow: {
		display: "flex",
	},
	dayBordersRow: {
		display: "flex",
	},
	dayColumn: (props: StyleProps) => ({
		width: props.dayWidth,
		display: "flex",
		flexDirection: "column",
		fontSize: "10px",
		fontWeight: 500,
		letterSpacing: 0,
		lineHeight: 1,
		color: getColorRgbaFromHex(color.text, 0.5),
		textAlign: "center",
		"&:first-child": {
			width: props.dayWidth - 2,
		},
		"&:last-child": {
			width: props.dayWidth - 1,
		},
	}),
	dayBorderColumn: (props: StyleProps) => ({
		width: props.dayWidth - 1,
		display: "flex",
		flexDirection: "column",
		height: px.xs,
		borderLeft: `1px solid ${color.tableCellBorder}`,
		"&:first-child": {
			width: props.dayWidth - 2,
			borderLeft: "0 none",
		},
		"&:last-child": {
			width: props.dayWidth - 2,
		},
	}),
});
